import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MagicVerify() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | error
  const [error, setError] = useState("");

  useEffect(() => {
    // Guard against React StrictMode double-mount in dev — the magic link is
    // single-use, so the second mount would otherwise see "already used".
    const guardKey = `lf_magic_consumed_${token}`;
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "1");

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/customer/auth/verify`, { token }, { withCredentials: true });
        if (cancelled) return;
        if (res.data?.session_token) {
          localStorage.setItem("lf_customer_session", res.data.session_token);
          localStorage.setItem("lf_customer_email", res.data.email || "");
        }
        navigate("/dashboard", { replace: true });
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e.response?.data?.detail || "Could not verify the link.");
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="magic-verify-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-3xl p-8 text-center">
        {status === "verifying" ? (
          <>
            <Loader2 size={28} className="animate-spin text-zinc-400 mx-auto mb-4" />
            <div className="font-bold text-zinc-950">Signing you in…</div>
            <p className="text-sm text-zinc-500 mt-1">This will only take a moment.</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div className="font-bold text-zinc-950">Sign-in link is invalid</div>
            <p className="text-sm text-zinc-600 mt-1" data-testid="magic-verify-error">{error}</p>
            <button onClick={() => navigate("/customer/login")} data-testid="btn-back-to-login"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800">
              Request a new link <ArrowRight size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
